import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { UpdatedVessel } from '../../../types/vessel';
import { VesselTemplateSubPageContext } from './types';
import VesselForm from './VesselForm';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const EditVesselPage = () => {
  const { vessel, isFetching, error, updateContextVessel } =
    useOutletContext<VesselTemplateSubPageContext>();
  const { t } = useLanguage();
  const { updateVessel } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const updateVesselMutation = useMutation({
    mutationFn: updateVessel,
  });

  return (
    <VesselForm
      onSubmit={(values, callback) => {
        const updatedVessel: UpdatedVessel = {
          id: vessel.id,
          name: values.name,
          flag: values.flag,
          yearOfBuild: values.yearOfBuild,
          beam: values.beam,
          horsePower: values.horsePower,
          ratedEngineSpeed: values.ratedEngineSpeed,
          fuelType: values.fuelType,
          averageFuelConsumption: values.averageFuelConsumption,
          manningScale: values.manningScale,
          detailsOfConversion: values.detailsOfConversion,
          currentSurveyStatus: values.currentSurveyStatus,
          dryDockingSchedule: values.dryDockingSchedule,
          dateOfLastDryDocking: values.dateOfLastDryDocking,
          vesselOwner: values.vesselOwner,
          detailsOfDetentions: values.detailsOfDetentions,
          passengerCarriageCapacity: values.passengerCarriageCapacity,
          averageCrossingTime: values.averageCrossingTime,
          lengthOverall: values.lengthOverall,
          loadDraft: values.loadDraft,
          bunkerCapacity: values.bunkerCapacity,
          detailsOfCargoGear: values.detailsOfCargoGear,
          cargoCapacity: values.cargoCapacity,
        };

        updateVesselMutation.mutate(updatedVessel, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.vessel-update-success'),
            });
            // A successful update will just update the item cached data
            updateContextVessel({
              ...vessel,
              ...updatedVessel,
            });
            callback();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(error, t('content.vessel-update-error')),
            });
            updateVesselMutation.reset();
          },
        });
      }}
      vessel={vessel}
      isLoading={isFetching || updateVesselMutation.isPending}
      disabled={Boolean(error)}
    />
  );
};

export default EditVesselPage;
